/**
 * Treydora Chronicles — TreydoraEdBackend public blog API.
 * Env: REACT_APP_API_BASE_URL
 *
 * Response shape: { success, message, data } — we unwrap `data`.
 */
import { getApiBaseUrl } from "../utils/getApiBaseUrl";

const ALLOWED_QUERY_KEYS = new Set([
  "page",
  "limit",
  "category",
  "tag",
  "featured",
  "pinned",
  "search",
  "sort",
  "target",
]);

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function unwrapEnvelope(payload) {
  if (payload && payload.success === false) {
    const err = new Error(payload.message || "Request failed");
    err.code = payload.code;
    throw err;
  }
  return payload?.data;
}

function buildQueryString(params) {
  const search = new URLSearchParams();
  if (!params || typeof params !== "object") return "";
  Object.entries(params).forEach(([k, v]) => {
    if (!ALLOWED_QUERY_KEYS.has(k)) return;
    if (v === undefined || v === null || v === "") return;
    search.set(k, String(v));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * @param {Record<string, string | number | boolean | undefined>} [params]
 * @returns {Promise<{ posts: object[], total: number, page: number, totalPages: number, limit: number }>}
 */
export async function getPublishedChronicles(params = {}) {
  const base = getApiBaseUrl();
  const merged = { ...params, target: "treydoraChronicles" };
  const url = `${base}/api/blog/posts${buildQueryString(merged)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(
      payload?.message || payload?.error || `Failed to load chronicles (${response.status})`
    );
  }
  const data = unwrapEnvelope(payload);
  if (!data || typeof data !== "object") {
    return { posts: [], total: 0, page: 1, totalPages: 1, limit: 10 };
  }
  return {
    posts: Array.isArray(data.posts) ? data.posts : [],
    total: Number(data.total) || 0,
    page: Number(data.page) || 1,
    totalPages: Number(data.totalPages) || 1,
    limit: Number(data.limit) || 10,
  };
}

export async function getChronicleBySlug(slug) {
  const base = getApiBaseUrl();
  const segment = encodeURIComponent(String(slug || "").trim().toLowerCase());
  const url = `${base}/api/blog/posts/${segment}?target=treydoraChronicles`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    const err = new Error(
      payload?.message || payload?.error || `Chronicle not found (${response.status})`
    );
    err.status = response.status;
    throw err;
  }
  return unwrapEnvelope(payload);
}

/**
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function getChronicleCategories() {
  const base = getApiBaseUrl();
  const url = `${base}/api/blog/categories`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(
      payload?.message || payload?.error || `Failed to load categories (${response.status})`
    );
  }
  const data = unwrapEnvelope(payload);
  return Array.isArray(data) ? data : [];
}
